import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';

const PAGE_SIZE = 15;

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

export default function Tenants() {
  const [tenants, setTenants] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterPlan, setFilterPlan] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get<any[]>('/tenants').then(setTenants).finally(() => setIsLoading(false));
  }, []);

  const filtered = useMemo(() => tenants.filter(t => {
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.domains?.[0]?.toLowerCase().includes(search.toLowerCase());
    const matchPlan = !filterPlan || t.plan === filterPlan;
    const matchStatus = !filterStatus || t.status === filterStatus;
    return matchSearch && matchPlan && matchStatus;
  }), [tenants, search, filterPlan, filterStatus]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [search, filterPlan, filterStatus]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Tenants ({tenants.length})</h1>
        <Link to="/admin/provision" className="btn-primary flex items-center gap-1.5 text-sm">
          <Plus className="w-4 h-4" /> Nieuwe tenant
        </Link>
      </div>
      <div className="card p-4 flex flex-col sm:flex-row gap-3">
        <input type="text" placeholder="Zoek op naam of domein..." value={search} onChange={e => setSearch(e.target.value)} className="input flex-1" />
        <select value={filterPlan} onChange={e => setFilterPlan(e.target.value)} className="input sm:w-40">
          <option value="">Alle plans</option>
          <option value="free">Free</option><option value="pro">Pro</option><option value="business">Business</option>
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="input sm:w-44">
          <option value="">Alle statussen</option>
          <option value="active">Actief</option><option value="suspended">Gesuspendeerd</option><option value="archived">Gearchiveerd</option>
        </select>
      </div>
      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Naam</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Opslag</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {isLoading ? [...Array(5)].map((_, i) => (
              <tr key={i}>{[...Array(5)].map((__, j) => <td key={j} className="px-6 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>)}</tr>
            )) : paginated.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-400">Geen tenants gevonden.</td></tr>
            ) : paginated.map(t => (
              <tr key={t.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <p className="text-sm font-medium text-gray-900">{t.name}</p>
                  <p className="text-xs text-gray-400">{t.domains?.[0] || t.slug}</p>
                </td>
                <td className="px-6 py-4 text-sm text-gray-700 capitalize">{t.plan}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${t.status === 'active' ? 'bg-green-100 text-green-700' : t.status === 'suspended' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-600'}`}>{t.status}</span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">{formatBytes(t.storage_used_bytes || 0)}</td>
                <td className="px-6 py-4 text-right">
                  <Link to={`/admin/tenants/${t.id}`} className="text-sm text-brand-600 font-medium hover:underline">Details</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
            <span className="text-xs text-gray-400">{filtered.length} tenant{filtered.length !== 1 ? 's' : ''}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-500 px-2">{page} / {totalPages}</span>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
